import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useListboxKeys from "../../utils/hooks/useListboxKeys";

// A native tooltip only where a label is actually cut off: on every row it
// would be noise over names already fully on screen.
const titleIfTruncated = (label) => (e) => {
  const text = e.currentTarget.querySelector("[data-label]");
  const cut =
    text &&
    (text.scrollWidth > text.clientWidth || text.scrollHeight > text.clientHeight);
  e.currentTarget.title = cut ? String(label) : "";
};

const optionTone = (o, active) => {
  if (active) return "bg-indigo-50 font-bold text-indigo-700";
  const color = o.context ? "text-slate-400" : o.strong ? "text-slate-800" : "text-slate-600";
  return `${o.strong ? "font-semibold" : ""} ${color}`;
};

// Single-select filter dropdown styled to the design system (indigo accent,
// rounded-xl trigger, check on the active option). Mirrors MultiSelect: the menu
// is portalled to <body> and positioned against the trigger, so it is never
// clipped by an overflow-hidden parent (e.g. the Users table card header).
// `options` = [{ value, label }]; `icon` is an optional leading node.
//
// `searchable` adds a type-ahead box at the top of the menu. It is OPT-IN, not
// automatic on length: the timetable pickers list every class (30+), every
// teacher with lessons (45+) and every curriculum line (hundreds), where an
// unfiltered menu means scrolling a list you already know the name of — while
// the four-option filters elsewhere in the app would only gain a box to tab
// through. `disabled` exists for the same surfaces, whose options arrive from a
// second request: a trigger that opens onto nothing reads as broken.
//
// A tree flattened into the list (OrgSelect: head office → regions → schools)
// sets `depth` on an option to indent it and `strong` to set it heavier, and
// passes `filterOptions(options, query)` in place of the label match, which
// would strip a matched school of the region it sits under. A row that filter
// keeps only as `context` is set back, and the cursor lands past it after each
// keystroke, so Enter picks what was typed, not the heading above it.
export default function FilterSelect({
  options = [],
  value,
  onChange,
  icon = null,
  placeholder = "",
  ariaLabel,
  minWidthClass = "min-w-[12rem]",
  className = "",
  searchable = false,
  searchPlaceholder = "",
  emptyText = "",
  disabled = false,
  filterOptions = null,
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const updateRect = () => {
      const el = rootRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      // A trigger in the right half opens its menu leftward, right edges
      // aligned: a menu wider than its trigger (sixty long school names) would
      // otherwise run off the screen. Either way it is capped at the room on
      // that side, and a longer label truncates.
      const alignEnd = r.left + r.width / 2 > vw / 2;
      setRect({
        left: alignEnd ? undefined : r.left,
        right: alignEnd ? vw - r.right : undefined,
        top: r.bottom + 4,
        width: r.width,
        maxWidth: (alignEnd ? r.right : vw - r.left) - 8,
        maxHeight: Math.max(160, window.innerHeight - r.bottom - 16),
      });
    };
    updateRect();
    // The menu's own list scrolling moves nothing, and re-measuring on it
    // re-renders every row on every scroll frame.
    const onScroll = (e) => {
      if (!menuRef.current?.contains(e.target)) updateRect();
    };

    const onDocMouseDown = (e) => {
      if (
        (rootRef.current && rootRef.current.contains(e.target)) ||
        (menuRef.current && menuRef.current.contains(e.target))
      ) {
        return;
      }
      setOpen(false);
    };
    // Capture phase so we also catch scrolling of an overflow parent, not just window.
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", updateRect);
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", updateRect);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open]);

  // The query is per-opening, not sticky: reopening the menu to pick a second
  // class should not present it pre-filtered by the first one's name.
  useEffect(() => {
    if (open) {
      // The menu is portalled and mounts a frame after `open` flips, so the ref
      // is only populated on the next tick.
      const id = window.setTimeout(() => searchRef.current?.focus(), 0);
      return () => window.clearTimeout(id);
    }
    setQuery("");
    return undefined;
  }, [open]);

  const current = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!searchable || !q) return options;
    if (filterOptions) return filterOptions(options, q);
    return options.filter((o) => String(o.label).toLowerCase().includes(q));
  }, [options, query, searchable, filterOptions]);

  const select = useCallback(
    (v) => {
      setOpen(false);
      triggerRef.current?.focus();
      if (v !== value) onChange(v);
    },
    [onChange, value, triggerRef],
  );

  // Where the cursor lands on opening and after each keystroke: the current
  // choice, or — when the filter kept headings only as context — the first row
  // that was actually typed.
  const landingIndex = useMemo(
    () =>
      visible.some((o) => o.context)
        ? visible.findIndex((o) => !o.context)
        : visible.findIndex((o) => o.value === value),
    [visible, value],
  );
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown,
    listboxId,
    optionId,
    activeDescendant,
  } = useListboxKeys({
    open,
    setOpen,
    triggerRef,
    listRef,
    count: visible.length,
    selectedIndex: landingIndex,
    onSelect: (i) => select(visible[i].value),
  });

  const menu =
    open && rect
      ? createPortal(
          <div
            ref={menuRef}
            style={{
              position: "fixed",
              left: rect.left,
              right: rect.right,
              top: rect.top,
              minWidth: rect.width,
              maxWidth: rect.maxWidth,
              maxHeight: rect.maxHeight,
              zIndex: 1000,
            }}
            className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg"
          >
            {searchable && (
              // Sticky rather than scrolling away with the options: on a 45-name
              // list the box you are typing in would otherwise leave the menu.
              <div className="shrink-0 border-b border-slate-100 p-2">
                <input
                  ref={searchRef}
                  type="search"
                  role="combobox"
                  aria-expanded="true"
                  aria-controls={listboxId}
                  aria-activedescendant={activeDescendant}
                  aria-autocomplete="list"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder={searchPlaceholder}
                  aria-label={searchPlaceholder || ariaLabel}
                  className="w-full rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-sm outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20"
                />
              </div>
            )}
            <ul
              id={listboxId}
              ref={listRef}
              role="listbox"
              className="min-h-0 flex-1 overflow-y-auto py-1.5"
            >
              {visible.length === 0 ? (
                <li role="status" className="px-3 py-2 text-xs font-medium text-slate-400">
                  {emptyText}
                </li>
              ) : (
                visible.map((o, i) => {
                  const active = o.value === value;
                  return (
                    <li key={String(o.value)}>
                      <button
                        id={optionId(i)}
                        data-lb-index={i}
                        type="button"
                        role="option"
                        tabIndex={-1}
                        aria-selected={active}
                        onClick={() => select(o.value)}
                        onMouseMove={() => setActiveIndex(i)}
                        onMouseEnter={titleIfTruncated(o.label)}
                        // px-3 is 0.75rem; each level steps in another 1rem.
                        style={o.depth ? { paddingLeft: `${0.75 + o.depth}rem` } : undefined}
                        className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition ${optionTone(o, active)} ${
                          i === activeIndex ? "bg-slate-100" : ""
                        }`}
                      >
                        {/* Two lines before an ellipsis: what tells two long
                            names apart is often their END — «…(ұлдар)» vs
                            «…(қыздар)» — and one line on a phone cuts it. */}
                        <span data-label className="min-w-0 line-clamp-2 break-words">
                          {o.label}
                        </span>
                        {active && (
                          <svg
                            className="ml-auto w-4 h-4 shrink-0 text-indigo-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className={`relative ${className}`} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        role={searchable ? undefined : "combobox"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={searchable ? undefined : activeDescendant}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onMouseEnter={titleIfTruncated(current ? current.label : placeholder)}
        className={`flex items-center gap-2 ${minWidthClass} rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/30`}
      >
        {icon && <span className="shrink-0 text-slate-400">{icon}</span>}
        <span data-label className="truncate">{current ? current.label : placeholder}</span>
        <svg
          className={`ml-auto w-4 h-4 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {menu}
    </div>
  );
}
